import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { supervisorsGroupsApi } from 'services/apiDigitalTicket/supervisorsGroups';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

import * as GroupsActions from './actions';
import { GroupsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof GroupsActions.listGroupsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando grupos' }));
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(supervisorsGroupsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        GroupsActions.listGroupsSuccess({
          groupsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('supervisorsGroups - listGroups - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.listGroupsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof GroupsActions.filterGroupsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(supervisorsGroupsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        GroupsActions.filterGroupsSuccess({
          groupsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('supervisorsGroups - filterGroups - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    meta.promise.reject(error);
    yield put(GroupsActions.filterGroupsFailure());
  }
}

function* get({
  payload,
}: ReturnType<typeof GroupsActions.getGroupRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(supervisorsGroupsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(GroupsActions.getGroupSuccess({ groupData: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('supervisorsGroups - getGroup - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.getGroupFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof GroupsActions.createGroupRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando grupo' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(supervisorsGroupsApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(GroupsActions.createGroupSuccess({ groupData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Grupo criado com sucesso',
        }),
      );
      yield put(push(PATH.COMPANY_MANAGEMENT.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('supervisorsGroups - createGroup- error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.createGroupFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof GroupsActions.updateGroupRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(supervisorsGroupsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(GroupsActions.updateGroupSuccess({ groupData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Grupo alterado com sucesso',
        }),
      );
      yield put(
        GroupsActions.listGroupsRequest({ body: { _includes: 'user' } }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('supervisorsGroups - updateGroup - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.updateGroupFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof GroupsActions.deleteGroupRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo grupo' }));
    const response = yield call(supervisorsGroupsApi.delete, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(GroupsActions.deleteGroupSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Grupo excluído com sucesso',
        }),
      );
      yield put(
        GroupsActions.listGroupsRequest({ body: { _includes: 'user' } }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('supervisorsGroups - deleteGroup - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.deleteGroupFailure());
    yield put(DialogsActions.setClose());
  }
}

export const supervisorsGroupsSagas = [
  takeLatest(GroupsTypes.LIST_GROUPS_REQUEST, list),
  takeLatest(GroupsTypes.FILTER_GROUPS_REQUEST, filter),
  takeLatest(GroupsTypes.GET_GROUP_REQUEST, get),
  takeLatest(GroupsTypes.CREATE_GROUP_REQUEST, create),
  takeLatest(GroupsTypes.UPDATE_GROUP_REQUEST, update),
  takeLatest(GroupsTypes.DELETE_GROUP_REQUEST, remove),
];
